import NextLink from 'next/link';
import { Text } from 'shared/components/text';
import type { LinkProps } from './index';
import { MouseEventHandler } from 'react';
import { clsx } from 'clsx';

const Link = ({
  href,
  asHref,
  children,
  asBlock = false,
  asInline = false,
  shallow = false,
  skipDefaults = false,
  openInNewTab = false,
  disabled = false,
  containerClassName = '',
  textClassName = '',
  numberOfLines,
  testID,
  accessibilityLabel,
  onPress,
}: LinkProps) => {
  const isHashLink = href.startsWith('#');
  const handleClick: MouseEventHandler = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onPress?.();
  };
  const className = clsx(`group ${containerClassName}`, {
    'inline-flex': !asBlock && !asInline,
    'flex flex-col': asBlock,
    'cursor-default': disabled,
  });
  const content =
    asBlock || asInline ? (
      children
    ) : (
      <Text
        skipDefaults={skipDefaults}
        numberOfLines={numberOfLines}
        className={clsx(textClassName, { 'hover:underline': !disabled })}
      >
        {children}
      </Text>
    );

  if (isHashLink) {
    return (
      <a
        href={href}
        className={className}
        data-testid={testID}
        aria-label={accessibilityLabel}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  return (
    <NextLink
      // Pages Router still prefetches on hover/touch; avoid loading every visible link.
      prefetch={false}
      target={openInNewTab ? '_blank' : undefined}
      href={href}
      as={asHref}
      className={className}
      shallow={shallow}
      data-testid={testID}
      aria-label={accessibilityLabel}
      onClick={handleClick}
    >
      {content}
    </NextLink>
  );
};

export { Link };
